// src/prototype/components/EAContextReviewCard.tsx
// ============================================================
// EA CONTEXT REVIEW CARD
// Displays a single context attribute with source, confidence,
// and affordances for editing or flagging incorrect information.
// ============================================================

import React, { useState } from 'react';
import { Pencil, Flag, Check, X } from 'lucide-react';
import { EASourceBadge, SourceType } from './EASourceBadge';
import { EAConfidencePill, ConfidenceLevel } from './EAConfidencePill';
import { useEA, FlagType } from '../contexts/EAContext';

export interface ContextAttribute {
  id: string;
  label: string;
  value: string;
  source: SourceType;
  confidence?: ConfidenceLevel;
  category?: string;
  documentName?: string;
}

interface EAContextReviewCardProps {
  attribute: ContextAttribute;
  onEdit?: (id: string, newValue: string) => void;
  className?: string;
}

export function EAContextReviewCard({ attribute, onEdit, className = '' }: EAContextReviewCardProps) {
  const { addFlag, addEdit, flags } = useEA();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(attribute.value);
  const [showFlagMenu, setShowFlagMenu] = useState(false);

  // Check if this attribute has been flagged
  const existingFlag = flags.find(
    f => f.targetType === 'context' && f.targetId === attribute.id
  );

  const handleSaveEdit = () => {
    if (editValue !== attribute.value) {
      addEdit({
        targetType: 'context',
        targetId: attribute.id,
        originalValue: attribute.value,
        newValue: editValue,
      });
      onEdit?.(attribute.id, editValue);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditValue(attribute.value);
    setIsEditing(false);
  };

  const handleFlag = (flagType: FlagType) => {
    addFlag({
      targetType: 'context',
      targetId: attribute.id,
      flagType,
    });
    setShowFlagMenu(false);
  };

  const flagOptions: { type: FlagType; label: string }[] = [
    { type: 'incorrect', label: 'This is incorrect' },
    { type: 'missing', label: 'Missing information' },
  ];

  return (
    <div
      className={`
        relative bg-white rounded-xl border border-[#e5e7eb] p-4
        hover:shadow-md transition-shadow
        ${existingFlag ? 'ring-2 ring-amber-300' : ''}
        ${className}
      `}
    >
      {/* Category tag */}
      {attribute.category && (
        <span className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
          {attribute.category}
        </span>
      )}

      {/* Label */}
      <div className="flex items-start justify-between gap-2 mt-1">
        <h4 className="text-sm font-semibold text-[#18365E]">{attribute.label}</h4>
        
        {/* Badges */}
        <div className="flex items-center gap-1.5 shrink-0">
          <EASourceBadge source={attribute.source} documentName={attribute.documentName} />
          {attribute.confidence && (
            <EAConfidencePill level={attribute.confidence} showLabel={false} />
          )}
        </div>
      </div>

      {/* Value - editable */}
      <div className="mt-2">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-[#18365E] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E]/20"
              autoFocus
            />
            <button
              onClick={handleSaveEdit}
              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Save"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancelEdit}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <p className="text-sm text-[#374151]">{editValue}</p>
        )}
      </div>

      {/* Action buttons */}
      {!isEditing && (
        <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[#e5e7eb]">
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#18365E] hover:bg-[#F5F7FA] rounded-lg transition-colors"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </button>

          <div className="relative">
            <button
              onClick={() => setShowFlagMenu(!showFlagMenu)}
              className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                existingFlag
                  ? 'text-amber-600 bg-amber-50'
                  : 'text-[#717182] hover:bg-[#F5F7FA]'
              }`}
            >
              <Flag className="w-3 h-3" />
              {existingFlag ? 'Flagged' : 'Flag'}
            </button>

            {/* Flag dropdown */}
            {showFlagMenu && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-[#e5e7eb] rounded-lg shadow-lg z-10 min-w-[160px]">
                {flagOptions.map((option) => (
                  <button
                    key={option.type}
                    onClick={() => handleFlag(option.type)}
                    className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-[#F5F7FA] first:rounded-t-lg last:rounded-b-lg"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Flagged indicator */}
      {existingFlag && (
        <div className="absolute -top-2 -right-2 bg-amber-400 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
          !
        </div>
      )}
    </div>
  );
}

export default EAContextReviewCard;